// Lifted from schedugram-client/react-src/components/ui/notification.tsx
// — `font-inter` swapped for `font-gotham` (Inter is forbidden in this repo);
// FontAwesome icons via the kit script in index.html. Same accent-bar layout,
// same six variants (default / empty / success / info / warning / error).

import { forwardRef, useId, useState } from "react";

import { cn } from "@/lib/utils";
import {
  notificationVariants,
  type NotificationProps,
  type NotificationVariant,
} from "./notification-variants";

const accentVariants: Record<NotificationVariant, string> = {
  default: "bg-neutral-500",
  empty: "bg-neutral-500",
  success: "bg-green-600",
  info: "bg-blue-500",
  warning: "bg-yellow-600",
  error: "bg-red-500",
};

const iconVariants: Record<NotificationVariant, string> = {
  default: "fa-circle-info text-neutral-500",
  empty: "fa-circle-info text-neutral-500",
  success: "fa-circle-check text-green-600",
  info: "fa-circle-info text-blue-500",
  warning: "fa-triangle-exclamation text-yellow-600",
  error: "fa-circle-xmark text-red-500",
};

const actionButtonClass =
  "ml-auto flex size-5 shrink-0 cursor-pointer items-center justify-center self-start rounded border-none bg-transparent p-0 text-base text-neutral-500 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:outline-none";

const Notification = forwardRef<HTMLDivElement, NotificationProps>(
  (
    {
      variant = "default",
      title,
      description,
      className,
      expandable = false,
      defaultExpanded = true,
      onDismiss,
    },
    ref,
  ) => {
    const descriptionId = useId();
    const [expanded, setExpanded] = useState(defaultExpanded);

    const role =
      variant === "warning" || variant === "error"
        ? "alert"
        : variant === "info" || variant === "success"
          ? "status"
          : undefined;

    const hasDescription = description != null;
    const showExpandToggle = expandable && hasDescription;
    // For the disclosure pattern we keep the description in the DOM and hide
    // it via the `hidden` attribute when collapsed, so `aria-controls` always
    // resolves to a real element. Non-expandable notifications still drop the
    // node entirely when there's no description.
    const renderDescription = hasDescription;
    const descriptionHidden = expandable && !expanded;
    const titleIconClassName = cn(
      "fa-regular flex size-5 shrink-0 items-center justify-center self-start text-base leading-none",
      iconVariants[variant],
    );

    return (
      <div ref={ref} role={role} className={cn(notificationVariants({ variant }), className)}>
        <div
          className={cn("w-0.5 shrink-0 self-stretch rounded-[3px]", accentVariants[variant])}
          aria-hidden="true"
        />

        <div className="flex min-w-0 flex-1 flex-col gap-1 py-1">
          <div className="flex w-full items-center gap-2">
            <i className={titleIconClassName} aria-hidden="true" />
            <p className="m-0 flex-1 font-gotham text-sm leading-5 font-medium text-neutral-800">
              {title}
            </p>
          </div>
          {renderDescription && (
            <div
              id={showExpandToggle ? descriptionId : undefined}
              hidden={descriptionHidden}
              className="m-0 font-gotham text-sm leading-5 font-normal text-neutral-500"
            >
              {description}
            </div>
          )}
        </div>

        {showExpandToggle && (
          <button
            type="button"
            className={cn(actionButtonClass, "text-neutral-500")}
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            aria-controls={descriptionId}
            aria-label={expanded ? "Collapse details" : "Expand details"}
          >
            <i
              className={`fa-solid fa-chevron-${expanded ? "up" : "down"} leading-none`}
              aria-hidden="true"
            />
          </button>
        )}

        {onDismiss && (
          <button
            type="button"
            className={actionButtonClass}
            onClick={onDismiss}
            aria-label="Dismiss notification"
          >
            <i className="fa-solid fa-xmark leading-none" aria-hidden="true" />
          </button>
        )}
      </div>
    );
  },
);
Notification.displayName = "Notification";

export { Notification };
